import React, { useState, useEffect } from 'react';
import QueueService from './QueueService';
import safeLocalStorage from '../utils/safeStorage';
import './OutletBadge.css';

export default function OutletBadge({ businessName, businessId, pageTitle, className = '' }) {
  const [currentName, setCurrentName] = useState(() => {
    return businessName || safeLocalStorage.getItem('businessName') || '';
  });

  const [currentBizId, setCurrentBizId] = useState(() => {
    return businessId || safeLocalStorage.getItem('businessId') || '';
  });

  const [bizList, setBizList] = useState(() => {
    try {
      const stored = safeLocalStorage.getItem('businessIds');
      if (stored) {
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) { }
    return [];
  });

  useEffect(() => {
    if (businessName) {
      setCurrentName(businessName);
      safeLocalStorage.setItem('businessName', businessName);
    }
    if (businessId) {
      setCurrentBizId(businessId);
    }
  }, [businessName, businessId]);

  useEffect(() => {
    // If name is not available, try to resolve from localStorage or API
    const targetId = currentBizId || safeLocalStorage.getItem('businessId');
    if (!currentName && targetId) {
      QueueService.GetBusinessInfo(parseInt(targetId, 10))
        .then((info) => {
          if (info && info.business_name) {
            setCurrentName(info.business_name);
            safeLocalStorage.setItem('businessName', info.business_name);
          }
        })
        .catch(() => { });
    }

    // Refresh bizList if not populated
    if (bizList.length === 0) {
      try {
        const stored = safeLocalStorage.getItem('businessIds');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setBizList(parsed);
          }
        }
      } catch (e) { }
    }
  }, [currentBizId, currentName, bizList.length]);

  // Update browser tab document title with Outlet name if pageTitle is provided
  useEffect(() => {
    if (pageTitle) {
      const titleOutlet = currentName ? `${currentName} • ` : '';
      document.title = `${titleOutlet}${pageTitle} | WaitWhiz`;
    }
  }, [pageTitle, currentName]);

  const handleSwitch = (e) => {
    const newId = parseInt(e.target.value, 10);
    if (!newId || newId === parseInt(currentBizId, 10)) return;

    safeLocalStorage.setItem('businessId', newId.toString());
    const matched = bizList.find((b) => (typeof b === 'object' ? b.id : b) === newId);
    if (matched && matched.name) {
      safeLocalStorage.setItem('businessName', matched.name);
    }
    window.location.reload();
  };

  const hasMultiple = bizList && bizList.length > 1;

  return (
    <div className={`outlet-badge-container ${className}`}>
      <div
        className={`outlet-badge-pill ${hasMultiple ? 'outlet-badge-pill-interactive' : ''}`}
        title={hasMultiple ? 'Active Outlet: Tap to switch' : `Active Outlet: ${currentName || 'Loading...'}`}
        role="region"
        aria-label="Active Outlet Indicator"
      >
        <div className="outlet-badge-content">
          <span className="outlet-badge-dot" aria-hidden="true" />
          <span className="outlet-badge-kicker">OUTLET</span>
          <span className="outlet-badge-icon" role="img" aria-label="Location">📍</span>
          <span className="outlet-badge-text" title={currentName || 'Outlet'}>
            {currentName || 'Loading outlet...'}
          </span>
        </div>

        {hasMultiple && (
          <div className="outlet-badge-switch-action">
            <span className="outlet-badge-switch-text">Switch</span>
            <span className="outlet-badge-chevron" aria-hidden="true">▾</span>
            <select
              className="outlet-badge-select"
              value={currentBizId || ''}
              onChange={handleSwitch}
              aria-label="Switch Active Outlet"
            >
              {bizList.map((biz) => {
                const id = typeof biz === 'object' ? biz.id : biz;
                const name = typeof biz === 'object' ? (biz.name || `Outlet #${biz.id}`) : `Outlet #${biz}`;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
